import React, { useRef } from "react";
import type { TextAreaRef } from "antd/lib/input/TextArea";
import { observer } from "mobx-react-lite";
import { Alert, Button, Form, Input, Typography } from "antd";
import AppModal from "../../common/components/AppModal";
import WorkbenchNotesStore, {
    WORKBENCH_NOTE_MAX_LENGTH,
} from "../stores/WorkbenchNotesStore";

type Props = {
    store: WorkbenchNotesStore;
    caseTitle: string;
    onSaved?: () => void;
};

const QuickNoteContext = observer(({ store, caseTitle }: Props) => (
    <>
        <Typography.Paragraph type="secondary">
            {store.formSubmissionIdentifier
                ? "Visible in Workbench for this submission"
                : "Visible in Workbench for this case"}
        </Typography.Paragraph>
        <div className="workbench-note-context">
            <div>{store.formSubmissionIdentifier ? "Submission" : "Case"}</div>
            <strong>{store.formSubmissionIdentifier ?? caseTitle}</strong>
            <div>
                {store.needsContextReview
                    ? "Review the current case context before saving"
                    : store.formSubmissionIdentifier
                      ? caseTitle
                      : "Linked directly to this case (no Submission)"}
            </div>
        </div>
    </>
));

const WorkbenchQuickNote: React.FC<Props> = ({ store, caseTitle, onSaved }) => {
    const noteInput = useRef<TextAreaRef>(null);
    return (
        <>
            <Button
                className="workbench-new-subject-action-row"
                icon={
                    <span className="workbench-new-subject-action-glyph workbench-new-subject-action-glyph-note" />
                }
                onClick={() => void store.start()}
                loading={store.opening}
                type="link"
            >
                <span className="workbench-new-subject-action-label">
                    Add quick note
                </span>
            </Button>
            <AppModal
                open={store.open}
                afterOpenChange={(visible) => {
                    if (visible) noteInput.current?.focus();
                }}
                title="Add quick note"
                width={520}
                onCancel={store.cancel}
                closable={!store.saving}
                maskClosable={!store.saving}
                keyboard={!store.saving}
                cancelButtonTitle="Cancel"
                cancelButtonProps={{ disabled: store.saving }}
                onCancelClick={store.cancel}
                submitButtonTitle={
                    store.recovering ? "Retry save" : "Save note"
                }
                submitButtonProps={{
                    htmlType: "button",
                    loading: store.saving,
                    disabled: !store.valid || store.needsContextReview,
                }}
                onSubmitClick={async () => {
                    if (await store.save()) onSaved?.();
                }}
                data-id-name="workbench-quick-note"
            >
                <QuickNoteContext store={store} caseTitle={caseTitle} />
                {store.saveError && !store.needsContextReview && (
                    <Alert type="error" showIcon message={store.saveError} />
                )}
                {store.needsContextReview && (
                    <Alert
                        type="warning"
                        showIcon
                        message="The case context changed. Review the current case, then save your draft."
                        action={
                            <Button
                                onClick={() => void store.reviewContext()}
                                loading={store.loading}
                            >
                                Review current case
                            </Button>
                        }
                    />
                )}
                {store.open && store.loadError && (
                    <Alert type="error" showIcon message={store.loadError} />
                )}
                {store.recovering && (
                    <Alert
                        type="info"
                        message="The save may have completed. Retry the same note to recover its result. Cancel closes this draft; it cannot undo a save that already completed."
                    />
                )}
                <Form layout="vertical">
                    <Form.Item
                        label={<span className="dialog-field-label">Note</span>}
                        htmlFor="workbench-note-text"
                        required
                        extra="This note stays in Workbench."
                    >
                        <Input.TextArea
                            ref={noteInput}
                            id="workbench-note-text"
                            aria-label="Note"
                            aria-required="true"
                            placeholder={
                                store.formSubmissionIdentifier
                                    ? "Write a note for this submission"
                                    : "Write a note for this case"
                            }
                            value={store.draft}
                            onChange={(event) =>
                                store.setDraft(event.target.value)
                            }
                            disabled={store.draftLocked}
                            maxLength={WORKBENCH_NOTE_MAX_LENGTH}
                            autoFocus
                            showCount
                            autoSize={{ minRows: 4, maxRows: 10 }}
                        />
                    </Form.Item>
                </Form>
            </AppModal>
        </>
    );
};

export default observer(WorkbenchQuickNote);
